import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Breadcrumbs,
  Button,
  SearchWithFilter,
  type FilterSelection,
} from '@enamelai/design-system'
import { Icon as AddIcon } from '@enamelai/design-system/icons/Add'
import { Icon as FileIcon } from '@enamelai/design-system/icons/File'
import FormTemplatePreview from './FormTemplatePreview.component'
import FormPreview from '@components/pages/FormBuilder/FormContentArea/FormPreview.component'
import { schemaToPreview } from './templatePreviewMock.data'
import { getFormTemplates } from '@services/api/forms.api'
import type { IFormTemplate } from '@dataTypes/forms.type'
import { FormTemplateStatus } from '@dataTypes/forms.type'
import type { GroupField } from '@components/pages/FormBuilder/types'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import './Templates.css'

const GLOBAL_LOADING_CONTAINER = 'body'

/** Parse schema_json from API to GroupField[] for FormPreview. Returns null if missing or invalid. */
function parseSchemaToGroups(
  schemaJson: string | GroupField[] | { groups: GroupField[] } | undefined,
): GroupField[] | null {
  if (schemaJson == null) return null
  if (Array.isArray(schemaJson)) {
    return schemaJson.length > 0 ? schemaJson : null
  }
  if (typeof schemaJson === 'object' && 'groups' in schemaJson) {
    return Array.isArray(schemaJson.groups) && schemaJson.groups.length > 0
      ? schemaJson.groups
      : null
  }
  try {
    const parsed = JSON.parse(schemaJson) as
      | GroupField[]
      | { groups?: GroupField[] }
    const groups = Array.isArray(parsed) ? parsed : parsed.groups
    return Array.isArray(groups) && groups.length > 0 ? groups : null
  } catch {
    return null
  }
}

interface TemplateItem {
  id: string
  name: string
  description: string
  isCreateNew?: boolean
  /** From API; used to render form preview in the card */
  schemaJson?: string | GroupField[] | { groups: GroupField[] }
}

interface TemplateSection {
  title: string
  items: TemplateItem[]
}

/** Map category_title from API to Templates page section title (form type only; Drafts is status-based) */
const CATEGORY_TO_SECTION_TITLE: Record<string, string> = {
  'Patient Information': 'Patient Information Forms Templates',
  'Consent Forms': 'Consent Forms Templates',
  Others: 'Others',
}

const SECTION_ORDER = [
  'Patient Information Forms Templates',
  'Consent Forms Templates',
  'Others',
  'Drafts',
]

const FILTER_LABELS = { section: 'Category' }
const FILTER_OPTIONS = { section: SECTION_ORDER }

export interface TemplatesNavigationContext {
  formTitle?: string
  templateId?: string
  categoryTitle?: string
}

interface TemplatesProps {
  onOpenCreateFromTemplate?: (context: TemplatesNavigationContext) => void
  onBackToDashboard?: () => void
}

/** Keep only the latest version per template so the list shows one row per template. */
function latestFormTemplatesOnly(templates: IFormTemplate[]): IFormTemplate[] {
  const byKey = new Map<string, IFormTemplate>()
  for (const t of templates) {
    const row = t as IFormTemplate & { templateKey?: string }
    const templateKey = t.template_key ?? row.templateKey
    const fallbackKey = `${t.category_title ?? ''}|${(t.title ?? '').trim().toLowerCase()}`
    const key =
      templateKey != null && templateKey !== ''
        ? String(templateKey)
        : fallbackKey
    const existing = byKey.get(key)
    const ver = t.version ?? 0
    if (!existing || (existing.version ?? 0) < ver) {
      byKey.set(key, t)
    }
  }
  return Array.from(byKey.values())
}

function toTemplateItem(t: IFormTemplate): TemplateItem {
  return {
    id: t.form_template_id ?? '',
    name: t.title,
    description: `${t.title} form template`,
    isCreateNew: false,
    schemaJson: t.schema_json,
  }
}

function groupTemplatesByCategory(
  templates: IFormTemplate[],
): TemplateSection[] {
  const byCategory = new Map<string, TemplateItem[]>()
  const draftTemplates = templates.filter(
    (t) => t.status === FormTemplateStatus.Draft,
  )
  const nonDraftTemplates = templates.filter(
    (t) => t.status !== FormTemplateStatus.Draft,
  )

  for (const t of nonDraftTemplates) {
    const sectionTitle = CATEGORY_TO_SECTION_TITLE[t.category_title] ?? 'Others'
    const list = byCategory.get(sectionTitle) ?? []
    list.push(toTemplateItem(t))
    byCategory.set(sectionTitle, list)
  }

  const sections: TemplateSection[] = SECTION_ORDER.map((title) => {
    const items =
      title === 'Drafts'
        ? draftTemplates.map(toTemplateItem)
        : (byCategory.get(title) ?? [])
    return { title, items }
  })

  sections.forEach((sec) => {
    sec.items.push({
      id: `create-${sec.title}`,
      name: 'Create a new template',
      description: 'Create a custom template',
      isCreateNew: true,
    })
  })
  return sections
}

const Templates: React.FC<TemplatesProps> = ({
  onOpenCreateFromTemplate,
  onBackToDashboard,
}) => {
  const navigate = useNavigate()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [sections, setSections] = useState<TemplateSection[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilters, setSelectedFilters] = useState<FilterSelection>({})

  const visibleSections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const selectedSections = selectedFilters.section ?? []
    return sections
      .filter(
        (section) =>
          selectedSections.length === 0 ||
          selectedSections.includes(section.title),
      )
      .map((section) => ({
        ...section,
        // "Create a new template" cards only make sense in the unfiltered list.
        items: section.items.filter((item) =>
          query === ''
            ? true
            : !item.isCreateNew && item.name.toLowerCase().includes(query),
        ),
      }))
      .filter((section) => query === '' || section.items.length > 0)
  }, [sections, searchQuery, selectedFilters])

  const handleBack = (): void => {
    if (onBackToDashboard) {
      onBackToDashboard()
    } else {
      navigate('/form-builder')
    }
  }

  useEffect(() => {
    showLoading({ container: GLOBAL_LOADING_CONTAINER })
    getFormTemplates()
      .then((formTemplates) => {
        const latestOnly = latestFormTemplatesOnly(formTemplates)
        setSections(groupTemplatesByCategory(latestOnly))
      })
      .catch(() => setSections([]))
      .finally(() => {
        hideLoading(GLOBAL_LOADING_CONTAINER)
        setLoading(false)
      })
  }, [showLoading, hideLoading])

  const handleTemplateClick = (item: TemplateItem): void => {
    if (item.isCreateNew) {
      if (onOpenCreateFromTemplate) {
        onOpenCreateFromTemplate({ formTitle: 'Untitled' })
      } else {
        navigate('/form-builder/create', {
          state: { formTitle: 'Untitled' },
        })
      }
    } else {
      if (onOpenCreateFromTemplate) {
        onOpenCreateFromTemplate({
          templateId: item.id,
          formTitle: item.name,
        })
      } else {
        navigate(`/form-builder/view/${item.id}`)
      }
    }
  }

  return (
    <div className="form-builder-templates-page">
      <div className="form-builder-templates-header">
        <Breadcrumbs
          className="form-builder-breadcrumbs"
          items={[
            { id: 'forms', label: 'Forms', onClick: handleBack },
            { id: 'templates', label: 'Templates' },
          ]}
          showBackIcon
          onBackClick={handleBack}
        />
        <div className="form-builder-templates-header-actions">
          <SearchWithFilter
            className="form-builder-templates-search"
            placeholder="Search"
            ariaLabel="Search templates"
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={() => setSearchQuery('')}
            filters={FILTER_OPTIONS}
            filterLabels={FILTER_LABELS}
            selectedFilters={selectedFilters}
            onFilterChange={setSelectedFilters}
            onReset={() => setSelectedFilters({})}
            showResetButton
          />
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={() => {
              if (onOpenCreateFromTemplate) {
                onOpenCreateFromTemplate({ formTitle: 'Untitled' })
              } else {
                navigate('/form-builder/create')
              }
            }}
          >
            Create new form
          </Button>
        </div>
      </div>

      <div className="form-builder-templates-content">
        {!loading &&
          visibleSections.map((section) => (
            <div key={section.title} className="form-builder-templates-section">
              <div className="form-builder-templates-section-header">
                {section.title}
              </div>
              <div className="form-builder-templates-list">
                {section.items.map((item) => (
                  <div
                    key={item.id}
                    className={`form-builder-templates-item ${
                      item.isCreateNew
                        ? 'form-builder-templates-item-create'
                        : ''
                    }`}
                    role="button"
                    tabIndex={0}
                    aria-label={item.name}
                    onClick={() => handleTemplateClick(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleTemplateClick(item)
                      }
                    }}
                  >
                    <div
                      className={`form-builder-templates-item-preview ${
                        parseSchemaToGroups(item.schemaJson)
                          ? 'form-builder-templates-item-preview-actual'
                          : ''
                      }`}
                    >
                      {item.isCreateNew ? (
                        <span className="form-builder-templates-item-add">
                          <AddIcon aria-hidden />
                        </span>
                      ) : (
                        (() => {
                          const groups = parseSchemaToGroups(item.schemaJson)
                          if (groups) {
                            return (
                              <div className="form-preview-scaled-wrapper">
                                <FormPreview
                                  groups={groups}
                                  formTitle={item.name}
                                />
                              </div>
                            )
                          }
                          const previewFromSchema = schemaToPreview(
                            item.name,
                            item.schemaJson,
                          )
                          if (previewFromSchema) {
                            return (
                              <FormTemplatePreview
                                preview={previewFromSchema}
                              />
                            )
                          }
                          return (
                            <FileIcon
                              className="form-builder-templates-item-icon"
                              aria-hidden
                            />
                          )
                        })()
                      )}
                    </div>
                    <div className="form-builder-templates-item-text">
                      <div className="form-builder-templates-item-title">
                        {item.name}
                      </div>
                      <div className="form-builder-templates-item-description">
                        {item.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}

export default Templates
